/**
 * ตรรกะการแสดงผลของ Executive Dashboard (P2-10d) — pure function ล้วน ไม่มี React
 * และไม่มีการเรียก API เอง เหตุผลเดียวกับ `lib/forecasts/forecastsView.ts`: แยกสิ่ง
 * ที่ทดสอบได้โดยไม่ต้อง mount component ออกจากตัวหน้าจอ
 *
 * สามข้อที่ไฟล์นี้มีอยู่เพื่อทำ และทั้งสามมาจาก invariant ของ Work Order นี้ตรง ๆ:
 *
 * 1. **จัดรูปตัวเลขตามหน่วยที่แถวบอก** — `COUNT` เป็นจำนวนเต็ม ส่วน `AMOUNT`/`QTY`
 *    คงสเกล `NUMERIC(18, 4)` ไว้เต็ม ทำด้วยการตัด/เติมสตริงล้วน **ไม่ผ่าน `Number`
 *    หรือ `parseFloat`** เพราะ 18 หลักเกินความแม่นยำของ IEEE-754 double
 * 2. **ช่วงวันที่เริ่มต้นที่สมเหตุสมผลและถูกจำกัดขอบเขต** โดยไม่ต้องให้ผู้ใช้เลือก
 *    ก่อนเห็นอะไรเลย และปฏิเสธช่วงกลับด้านที่ฟอร์มเอง ก่อนที่คำขอจะออกเดินทาง
 * 3. **ป้ายกำกับ scope ที่อ่านออก** สำหรับข้อความบนหน้าจอที่บอกว่ากำลังดูอะไรอยู่ —
 *    ไม่ใช่แค่ค่าที่ตัวควบคุมถืออยู่
 */

import { EXECUTIVE_SUMMARY_MAX_RANGE_DAYS, type MetricUnit, type SnapshotScope } from './types';

/* ---------------------------------------------------------- ตัวเลข/หน่วย -- */

interface DecimalParts {
  readonly sign: string;
  readonly intPart: string;
  readonly fracPart: string;
}

/** แยกสตริงทศนิยมออกเป็นเครื่องหมาย/จำนวนเต็ม/เศษ — สตริงล้วน ไม่ผ่าน `Number` เลย */
function splitDecimal(value: string): DecimalParts {
  const negative = value.startsWith('-');
  const unsigned = negative ? value.slice(1) : value;
  const [intPart = '0', fracPart = ''] = unsigned.split('.');
  return { sign: negative ? '-' : '', intPart: intPart === '' ? '0' : intPart, fracPart };
}

function groupThousands(intPart: string): string {
  return intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

/**
 * `metric_value` ที่จัดรูปแล้วตามหน่วยของแถวนั้น — หน่วยมาจาก `metric_unit` ที่
 * เซิร์ฟเวอร์ส่งมาเสมอ ไม่ใช่ตารางที่ map จาก `metric_code`
 *
 * `COUNT` แสดงเป็นจำนวนเต็ม — ตัดส่วนทศนิยมทิ้ง (เซิร์ฟเวอร์รับประกันว่าเป็น
 * `.0000` เสมอสำหรับหน่วยนี้ ดู `metrics.py`) ส่วน `AMOUNT`/`QTY` คงสี่ตำแหน่ง
 * ทศนิยมของ `NUMERIC(18, 4)` ไว้เต็ม — `"1200.0000"` ต้องไม่กลายเป็น `"1200"`
 * และไม่กลายเป็น `"1200.0000000"` จากการปัดเศษของ float
 */
export function formatMetricValue(value: string, unit: MetricUnit): string {
  const { sign, intPart, fracPart } = splitDecimal(value);
  const groupedInt = groupThousands(intPart);
  if (unit === 'COUNT') {
    return `${sign}${groupedInt}`;
  }
  const frac = (fracPart.length >= 4 ? fracPart.slice(0, 4) : fracPart.padEnd(4, '0'));
  return `${sign}${groupedInt}.${frac}`;
}

/** ป้ายหน่วยที่อ่านออก — ใช้กำกับคอลัมน์ตัวเลข ไม่ใช่ตัวตัดสินการจัดรูป */
export function metricUnitLabel(unit: MetricUnit): string {
  switch (unit) {
    case 'QTY':
      return 'จำนวน';
    case 'AMOUNT':
      return 'มูลค่า (บาท)';
    case 'COUNT':
      return 'จำนวนเอกสาร';
  }
}

/* --------------------------------------------------------------- scope -- */

/** ป้าย scope ที่อ่านออก — ต้องปรากฏบนตัวหน้าจอเอง ไม่ใช่แค่ในตัวควบคุมที่เลือกมัน */
export function scopeLabel(scope: SnapshotScope): string {
  switch (scope) {
    case 'PLANT':
      return 'รายโรงงาน';
    case 'CONSOLIDATED':
      return 'ภาพรวมทั้งบริษัท (ทุกโรงงานรวมกัน)';
  }
}

/** คำอธิบายสั้นของ scope — ใช้เป็น hint ใต้ตัวควบคุม */
export function scopeHint(scope: SnapshotScope): string {
  switch (scope) {
    case 'PLANT':
      return 'ตัวเลขของแต่ละโรงงานแยกแถวกัน';
    case 'CONSOLIDATED':
      return 'ยอดรวมทั้งบริษัทที่ fold ไว้แล้วตั้งแต่ตอนบันทึกเหตุการณ์ — ไม่ใช่ผลรวมที่คำนวณซ้ำหน้าจอนี้';
  }
}

/* ------------------------------------------------------------ ช่วงวันที่ -- */

function pad2(value: number): string {
  return String(value).padStart(2, '0');
}

/** `YYYY-MM-DD` จาก `Date` — อ่านค่าปี/เดือน/วันตรง ๆ ไม่ผ่าน `toISOString()` ซึ่งเลื่อนวันได้ตาม timezone */
export function toCalendarDate(date: Date): string {
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}

/** ช่วงเริ่มต้นที่สมเหตุสมผล — 30 วันล่าสุดจนถึงวันนี้ (invariant: "default to a sensible recent window") */
export const DEFAULT_WINDOW_DAYS = 30;

export interface DateRange {
  readonly date_from: string;
  readonly date_to: string;
}

export function defaultDateRange(today: Date = new Date()): DateRange {
  const from = new Date(today.getFullYear(), today.getMonth(), today.getDate() - (DEFAULT_WINDOW_DAYS - 1));
  return { date_from: toCalendarDate(from), date_to: toCalendarDate(today) };
}

/** ช่วงกลับด้าน — `date_from` มาหลัง `date_to` — เทียบแบบสตริงได้เพราะเป็น `YYYY-MM-DD` ล้วน */
export function isReversedRange(dateFrom: string, dateTo: string): boolean {
  return dateFrom > dateTo;
}

/** จำนวนวันของช่วง แบบรวมวันแรกและวันสุดท้าย — คำนวณผ่าน UTC เพื่อไม่ให้ DST ของ browser ปนเข้ามา */
export function rangeLengthDays(dateFrom: string, dateTo: string): number {
  const from = Date.UTC(
    Number(dateFrom.slice(0, 4)),
    Number(dateFrom.slice(5, 7)) - 1,
    Number(dateFrom.slice(8, 10)),
  );
  const to = Date.UTC(
    Number(dateTo.slice(0, 4)),
    Number(dateTo.slice(5, 7)) - 1,
    Number(dateTo.slice(8, 10)),
  );
  return Math.round((to - from) / 86_400_000) + 1;
}

/** ช่วงกว้างเกินขอบเขตที่ contract รับ — ใช้ปฏิเสธที่ฟอร์มก่อนคำขอจะออกเดินทาง */
export function rangeExceedsMaxDays(dateFrom: string, dateTo: string): boolean {
  if (isReversedRange(dateFrom, dateTo)) {
    return false;
  }
  return rangeLengthDays(dateFrom, dateTo) > EXECUTIVE_SUMMARY_MAX_RANGE_DAYS;
}

/**
 * ช่วงที่ฟอร์มนี้ส่งคำขอได้ — ทั้งสองกฎรวมเป็นเงื่อนไขเดียว ใช้ปิดปุ่ม/ไม่ยิง effect
 * แทนที่จะปล่อยให้ผู้ใช้พบ `422` เป็นคำตอบแรกของทุกครั้งที่กรอกผิด
 */
export function isRequestableRange(dateFrom: string, dateTo: string): boolean {
  return !isReversedRange(dateFrom, dateTo) && !rangeExceedsMaxDays(dateFrom, dateTo);
}

/** ขอบบนของ `date_to` ที่ยังอยู่ในขอบเขตของ `date_from` — ใช้เป็น `max` ของ input วันที่ */
export function maxDateToFor(dateFrom: string): string {
  const from = new Date(
    Number(dateFrom.slice(0, 4)),
    Number(dateFrom.slice(5, 7)) - 1,
    Number(dateFrom.slice(8, 10)),
  );
  const upperBound = new Date(from);
  upperBound.setDate(upperBound.getDate() + (EXECUTIVE_SUMMARY_MAX_RANGE_DAYS - 1));
  return toCalendarDate(upperBound);
}
